/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useContext, useEffect } from 'react';
import type { AnyStyledComponent } from 'styled-components';
import styled from 'styled-components';
import { useI18n } from '@oneaudi/i18n-context';
import type { TrackingDataV2, TrackingEventV2 } from '@oneaudi/audi-tracking-service';
import {
  Avatar,
  Headline,
  Layout,
  Popover,
  RichText,
  Text,
  TextButton,
  TextLink,
} from '@oneaudi/unified-web-components';
import AppLoaderErrorBoundary from '../AppLoaderErrorBoundary/AppLoaderErrorBoundary';
import type { AudiUserMenuProps } from '../../interfaces/header-components.interfaces';
import { userMenuContent } from '../../i18n/usermenuContent';
import {
  HeaderStyledLogin,
  StyledPopoverContainer,
  StyledTextSupplierName,
  StylesDetailsLink,
} from '../AudiHeader/AudiHeaderStyles';
import { EventAction, myAudiPopoverClick } from '../../utils/tracking';
import { ServiceContext } from '../../context/ServiceContext';
import useToggle from '../../hooks/use-toggle';
import AvatarIcon from './AvatarIcon';
import { getFontStyle, NumbersSize } from '../../utils/theme-utils';
import { AUTH_OIDC_USER_INFO, NAVIGATION_CLASSNAME } from '../../utils/globals';
import {
  createResetActiveItemAction,
  createSetActiveItemAction,
} from '../../actions/header-actions';

import { removeSessionData } from '../../utils/session-utils';
import { useAuth } from '../../hooks/use-auth';
import { OidcUserContext } from '../../context';
import { useEnvConfig } from '../../hooks/use-env-config';
import { useUserInfo } from '../../hooks/use-user-info';
import { useUserUpdatedData } from '../../hooks/use-user-updated-data';

const StyledLayout = styled(Layout as AnyStyledComponent)`
  &.supplier {
    margin-top: ${({ theme }): string => theme.spacing.l};
    flex-wrap: wrap;
    & span,
    & p {
      margin-bottom: ${({ theme }): string => theme.spacing.xs};
    }
  }
  a.login-links,
  button.login-links {
    margin-bottom: ${({ theme }): string => theme.spacing.m};
    text-decoration: none;
    &:hover {
      color: ${({ theme }): string => theme.colors.colorHover};
    }
    &:last-child {
      margin-bottom: 0;
    }
    svg {
      margin-right: ${({ theme }): string => theme.spacing.s};
    }
  }
  width: 351px;
`;

const StyledAvatarIcon = styled.div`
  padding: ${({ theme }): string => theme.spacing.s};
  padding-top: ${({ theme }): string => (process.env.IS_LEGACY ? theme.spacing.s : '0')};
  & svg {
    color: ${({ theme }): string => (theme.type === 'legacy' ? 'inherit' : theme.colors.icon)};
  }
  ${({ theme }): string =>
    theme.type === 'alpha'
      ? `padding-left: ${theme.spacing.xs}; padding-right: ${theme.spacing.xs};`
      : ''};
`;

const StyledHeadline = styled(Headline)`
  margin: ${({ theme }): string => theme.spacing.m} 0;
`;

const StyledRichText = styled(RichText)`
  margin: 0;
  ul {
    margin: 0;
    padding-inline-start: ${({ theme }): string => theme.spacing.m};
    margin-block-end: ${({ theme }): string => theme.spacing.m};
    li {
      color: ${({ theme }): string => theme.colors.colorBaseGrey60};
      padding-inline-start: ${({ theme }): string => theme.spacing.xs};
      ${getFontStyle(NumbersSize.SMALL)};
    }
  }
`;

const AudiUserMenu: React.FC<AudiUserMenuProps> = ({
  isOneCms,
  headerDispatch,
  headerNavigationItemsAmount,
  isOpen,
  marketBaseUrl,
  redirectUrl,
  idPageUrl,
  homeUrl,
  useUserMenu,
  supplier,
  totalActionItems,
}) => {
  const { authService, trackingService, headerStateService } = useContext(ServiceContext);
  const { oidcUser, loading, error } = useContext(OidcUserContext);
  const { isAuthenticated } = useAuth();
  const { idk } = useEnvConfig();
  const userInfo = useUserInfo(oidcUser);
  const { personalData, personalDataLoading } = userInfo;
  // when user first time registering use useUserUpdatedData data
  const personalUpdatedData = useUserUpdatedData();
  const benefitHeadlineLabel = useI18n(userMenuContent.benefitHeadline);
  const benefitSubHeadlineLabel = useI18n(userMenuContent.benefitSubHeadline);
  const benefitContent = useI18n(userMenuContent.benefitContent);
  const benefitLoginLabel = useI18n(userMenuContent.benefitLogin);
  const benefitMoreLabel = useI18n(userMenuContent.benefitMore);
  const benefitMarketUrl = useI18n(userMenuContent.benefitMarketUrl);
  const benefitMyAudi = useI18n(userMenuContent.benefitMyAudi);
  const benefitHome = useI18n(userMenuContent.benefitHome);
  const benefitLogout = useI18n(userMenuContent.benefitLogout);
  const flyoutLabel = useI18n(userMenuContent.flyoutLabel);
  const isLegacy = process.env.IS_LEGACY;
  const [toggleElement, toggle] = useToggle();

  // Extract the first name and last name from personalData
  const initials =
    personalData?.firstName && personalData?.lastName
      ? `${personalData.firstName.normalize().charAt(0).toUpperCase()}${personalData.lastName
          .normalize()
          .charAt(0)
          .toUpperCase()}`
      : '';

  const updatedInitials =
    personalUpdatedData?.personalData?.firstName && personalUpdatedData?.personalData?.lastName
      ? `${personalUpdatedData?.personalData?.firstName.normalize().charAt(0).toUpperCase()}${personalUpdatedData?.personalData?.lastName
          .normalize()
          .charAt(0)
          .toUpperCase()}`
      : '';

  useEffect(() => {
    if (toggleElement) {
      headerDispatch(
        createSetActiveItemAction({
          anchor: null,
          headerStateService,
          index: -1,
          showSearch: false,
          showDealer: false,
          showDealerSearch: false,
          showLoginFlyout: true,
          showWistListFlyout: false,
          openDealerDetails: false,
        }),
      );
    } else {
      headerDispatch(createResetActiveItemAction(headerStateService));
    }
  }, [toggleElement]);

  const trackUserAuth = () => {
    const additionalObjects = {
      user: [
        {
          segment: {
            isLoggedIn: isAuthenticated,
          },
        },
      ],
    };
    const userAuthenticationStatusEvent: TrackingEventV2 = {
      eventInfo: {
        eventAction: EventAction.NONE_INTERACTION,
        eventName: isAuthenticated ? 'user logged in' : 'user logged out',
      },
      attributes: {
        componentName: EventAction.COMPONENT_NAME,
        label: '',
        targetURL: '',
        clickID: '',
        elementName: '',
        value: '',
        pos: '',
      },
    };
    trackingService?.track({
      event: userAuthenticationStatusEvent,
      componentUpdate: additionalObjects,
    });
  };

  useEffect(() => {
    // this useEffect Hook is going to execute trackAuth() every 30 Minutes whenever the user remains inactive
    // https://collaboration.msi.audi.com/confluence/pages/viewpage.action?spaceKey=EXCHANGE&title=20-08+-+User+Menu+-+Update+Login+Status
    const storageKey = 'auth-tracking-timestamp';
    const checkTimePassed = () => {
      const timeout = 30 * 60 * 1000; // thirty minutes in milliseconds
      const storedTimestamp = localStorage.getItem(storageKey);
      const currentTimestamp = new Date().getTime();
      if (isAuthenticated) {
        if (!storedTimestamp || currentTimestamp - parseInt(storedTimestamp, 10) >= timeout) {
          localStorage.setItem(storageKey, currentTimestamp.toString());
          // update auth status once only
          trackUserAuth();
        }
      }
    };
    checkTimePassed();
    const interval = setInterval(checkTimePassed, 60 * 1000); // one minutes in milliseconds (60 * 1000)
    return () => {
      clearInterval(interval);
    };
  }, [isAuthenticated]);

  const renderLoggedOutState = () => {
    const handleLoginClick = (event: React.MouseEvent) => {
      event.preventDefault();
      const loginClickEvent: TrackingDataV2 = {
        event: {
          eventInfo: {
            eventAction: EventAction.EXIT,
            eventName: 'one header - myaudi - click on login',
          },
          attributes: {
            componentName: EventAction.COMPONENT_NAME,
            label: benefitLoginLabel,
            targetURL: '',
            clickID: '',
            elementName: 'button',
            value: '',
            pos: '',
          },
        },
      };
      trackingService?.track(loginClickEvent);
      authService.login(undefined, typeof window !== 'undefined' ? homeUrl : undefined);
    };
    const handleRegistrationClick = (event: React.MouseEvent) => {
      event.preventDefault();
      const registrationClickEvent: TrackingDataV2 = {
        event: {
          eventInfo: {
            eventAction: EventAction.EXIT,
            eventName: 'one header - myaudi - click on register',
          },
          attributes: {
            componentName: EventAction.COMPONENT_NAME,
            label: benefitMoreLabel,
            targetURL: '',
            clickID: '',
            elementName: 'button',
            value: '',
            pos: '',
          },
        },
      };
      trackingService?.track(registrationClickEvent);
      authService.login(undefined, typeof window !== 'undefined' ? homeUrl : undefined);
    };

    const handleDetailsClick = () => {
      const detailsClickEvent: TrackingDataV2 = {
        event: {
          eventInfo: {
            eventAction: EventAction.EXIT,
            eventName: 'one header - myaudi - click on more information',
          },
          attributes: {
            componentName: EventAction.COMPONENT_NAME,
            label: benefitMarketUrl,
            targetURL: '',
            clickID: '',
            elementName: 'text link',
            value: '',
            pos: '',
          },
        },
      };
      trackingService?.track(detailsClickEvent);
    };

    return (
      <Popover
        content={
          <StyledPopoverContainer>
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
            <div role="region" tabIndex={0}>
              <StyledHeadline
                data-testid="FAUSERMENU_BENEFIT_HEADLINE"
                tag="h4"
                variant={{
                  order: '4',
                  type: 'headline',
                  weight: 'normal',
                }}
                role="heading"
                aria-label={benefitHeadlineLabel}
              >
                {benefitHeadlineLabel}
              </StyledHeadline>
              <Text tag="p" variant={{ order: '1', style: 'normal', type: 'copy' }}>
                {benefitSubHeadlineLabel}
              </Text>
              <StyledRichText
                className="richtext"
                data-testid="rich-text"
                margin={[0, 0, 0, 0]}
                markdown={`&nbsp;${benefitContent}`}
              />
            </div>

            {benefitMarketUrl && (
              <StylesDetailsLink
                aria-label="My Audi Page"
                data-testid="FAUSERMENU_DETAILSLINK"
                href={typeof marketBaseUrl === 'string' ? marketBaseUrl : ''}
                onClick={handleDetailsClick}
                iconPosition="trailing"
                icon="forward"
                size="md"
                target="_self"
                title="My Audi Page"
                variant="standalone"
                // @ts-expect-error
                underline={false}
              >
                {benefitMarketUrl}
              </StylesDetailsLink>
            )}
            <TextButton
              aria-label="My Audi Login"
              data-testid="FAUSERMENU_LOGINBUTTON"
              variant="primary"
              onClick={handleLoginClick}
              className="login-button"
            >
              {benefitLoginLabel}
            </TextButton>
            <TextButton
              onClick={handleRegistrationClick}
              aria-label="Registration Page"
              variant="secondary"
            >
              {benefitMoreLabel}
            </TextButton>
            <StyledLayout className="supplier">
              {Array.isArray(supplier) &&
                supplier.map((supplierItem) => {
                  return (
                    <>
                      <StyledTextSupplierName
                        tag="span"
                        variant={{ order: '2', style: 'normal', type: 'copy' }}
                      >
                        {supplierItem?.Name}
                      </StyledTextSupplierName>
                      <Text tag="p" variant={{ order: '2', style: 'normal', type: 'copy' }}>
                        <TextLink
                          aria-label={`Open ${supplierItem?.Name} in a new tab`}
                          data-testid="test-id"
                          href={supplierItem?.LinkUrl || ''}
                          id="id"
                          rel="noreferrer"
                          size="sm"
                          target="_blank"
                          title={supplierItem?.Name}
                          variant="standalone"
                        >
                          {supplierItem?.LinkLabel}
                        </TextLink>
                      </Text>
                    </>
                  );
                })}
            </StyledLayout>
          </StyledPopoverContainer>
        }
        id="myaudi-popover"
        onClose={() => {
          toggle(false);
          trackingService?.track(
            myAudiPopoverClick(
              'close myaudi popover',
              headerNavigationItemsAmount + totalActionItems,
            ),
          );
        }}
        placement="bottom"
        alignment="right"
        role="tabpanel"
        triggerElement={
          <div>
            <Avatar
              aria-label={flyoutLabel}
              label=""
              title={flyoutLabel}
              size={isLegacy ? 'md' : 'sm'}
              variant="logged-out"
              onClick={() => {
                toggle();
                trackingService?.track(
                  myAudiPopoverClick(
                    `${toggleElement ? 'close' : 'open'} myaudi popover`,
                    headerNavigationItemsAmount + totalActionItems,
                  ),
                );
              }}
              className={NAVIGATION_CLASSNAME}
              // @ts-expect-error
              selected={toggleElement}
            />
          </div>
        }
        withTip={isLegacy ? true : false}
        open={toggleElement}
      />
    );
  };

  const renderLoggedInState = () => {
    const handleLogoutClick = (event: React.MouseEvent) => {
      event.preventDefault();
      const LogoutClickEvent: TrackingDataV2 = {
        event: {
          eventInfo: {
            eventAction: EventAction.INTERNAL,
            eventName: 'one header - myaudi - click on logout',
          },
          attributes: {
            componentName: EventAction.COMPONENT_NAME,
            label: benefitLogout,
            targetURL: '',
            clickID: '',
            elementName: 'button',
            value: '',
            pos: '',
          },
        },
      };
      trackingService?.track(LogoutClickEvent);
      removeSessionData(AUTH_OIDC_USER_INFO.sessionKey);
      if (isOneCms) {
        authService.logout(redirectUrl || window?.location?.href);
      } else {
        authService.logout();
      }
    };
    const handleHomeClick = () => {
      const homeClickEvent: TrackingDataV2 = {
        event: {
          eventInfo: {
            eventAction: EventAction.INTERNAL,
            eventName: 'one header - myaudi - click on myaudi home',
          },
          attributes: {
            componentName: EventAction.COMPONENT_NAME,
            label: benefitHome,
            targetURL: '',
            clickID: '',
            elementName: 'button',
            value: '',
            pos: ``,
          },
        },
      };
      trackingService?.track(homeClickEvent);
    };

    const handleIdPageClick = () => {
      const idPageClickEvent: TrackingDataV2 = {
        event: {
          eventInfo: {
            eventAction: EventAction.INTERNAL,
            eventName: 'one header - myaudi - click on myaudi account',
          },
          attributes: {
            componentName: EventAction.COMPONENT_NAME,
            label: 'myAudi Account',
            targetURL: '',
            clickID: '',
            elementName: 'button',
            value: '',
            pos: `${headerNavigationItemsAmount + totalActionItems} - 1`,
          },
        },
      };
      trackingService?.track(idPageClickEvent);
    };

    return (
      <Popover
        content={
          <StyledLayout direction="column">
            {homeUrl && (
              <TextLink
                href={homeUrl}
                aria-label="myAudi Home"
                data-testid="FAUSERMENU_LINKPORTALHOME"
                onClick={handleHomeClick}
                variant="standalone"
                icon="car"
                iconPosition="leading"
                size="md"
                className="login-links"
              >
                {benefitHome}
              </TextLink>
            )}
            {idPageUrl !== undefined && (
              <TextLink
                href={idPageUrl}
                aria-label="myAudi Account"
                data-testid="FAUSERMENU_LINKPORTALACCOUNT"
                onClick={handleIdPageClick}
                variant="standalone"
                size="md"
                icon="user"
                iconPosition="leading"
                className="login-links"
              >
                {benefitMyAudi}
              </TextLink>
            )}
            <TextLink
              data-testid="FAUSERMENU_LINKLOGOUT"
              aria-label="Logout"
              onClick={handleLogoutClick}
              variant="standalone"
              icon="logout"
              iconPosition="leading"
              size="md"
              className="login-links"
            >
              {benefitLogout}
            </TextLink>
          </StyledLayout>
        }
        id="myaudi-popover"
        onClose={() => {
          toggle(false);
          trackingService?.track(
            myAudiPopoverClick(
              'close myaudi popover',
              headerNavigationItemsAmount + totalActionItems,
            ),
          );
        }}
        placement="bottom"
        alignment="right"
        triggerElement={
          <StyledAvatarIcon
            id="myaudiUserIcon"
            onClick={() => {
              toggle();
              trackingService?.track(
                myAudiPopoverClick(
                  `${toggleElement ? 'close' : 'open'} myaudi popover`,
                  headerNavigationItemsAmount + totalActionItems,
                ),
              );
            }}
          >
            <AvatarIcon
              idk={idk}
              initials={initials || updatedInitials}
              flyoutLabel={flyoutLabel}
              loading={loading || personalDataLoading}
              error={error}
            />
          </StyledAvatarIcon>
        }
        withTip={isLegacy ? true : false}
        open={toggleElement}
      />
    );
  };

  if (useUserMenu === undefined) {
    return null;
  }

  return (
    <AppLoaderErrorBoundary>
      <HeaderStyledLogin
        headerNavigationItemsAmount={headerNavigationItemsAmount}
        isOpen={isOpen}
        data-testid="FAUSERMENU_TOGGLELINK"
      >
        {useUserMenu ? (isAuthenticated ? renderLoggedInState() : renderLoggedOutState()) : null}
      </HeaderStyledLogin>
    </AppLoaderErrorBoundary>
  );
};

export default AudiUserMenu;
